import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService, LOLBASEntry } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Copy, Check, Search, Filter, ExternalLink, Shield, Database, Table, Grid3X3, X } from "lucide-react";
import { toast } from "../../components/ui/use-toast";

interface LOLBASCommand {
  Command: string;
  Description: string;
  Usecase: string;
  Category: string;
  Privileges: string;
  MitreID: string;
  OperatingSystem: string;
  Tags: Array<{ [key: string]: string }>;
}

const mitreTechniqueNames: Record<string, string> = {
  'T1218': 'System Binary Proxy Execution',
  'T1105': 'Ingress Tool Transfer',
  // Add more as needed
};

function getTechniqueName(id: string) {
  return mitreTechniqueNames[id] || '';
}

export const LOLBASPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lolbasData, setLolbasData] = useState<LOLBASEntry[]>([]);
  const [filteredData, setFilteredData] = useState<LOLBASEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedPrivileges, setSelectedPrivileges] = useState<string>("all");
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const [lolbasEnabled, setLolbasEnabled] = useState<boolean | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/signin');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Check if LOLBAS is enabled
  useEffect(() => {
    const checkLOLBASAccess = async () => {
      try {
        const settings = await methodologyService.getLOLBASSettings();
        setLolbasEnabled(settings.enabled);
        
        if (!settings.enabled) {
          setError("LOLBAS access is currently disabled by the administrator.");
          toast({
            title: "Access Denied",
            description: "LOLBAS access is currently disabled.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Failed to check LOLBAS access:", error);
        setError("Failed to verify LOLBAS access.");
      }
    };

    checkLOLBASAccess();
  }, []);

  // Load LOLBAS data
  useEffect(() => {
    if (lolbasEnabled === false) return; // Don't load data if disabled

    const loadLOLBASData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/lolbas-proxy');
        if (!response.ok) {
          throw new Error('Failed to fetch LOLBAS data');
        }
        const data: LOLBASEntry[] = await response.json();
        setLolbasData(data);
        setFilteredData(data);
      } catch (error) {
        console.error('Error loading LOLBAS data:', error);
        setError('Failed to load LOLBAS data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadLOLBASData();
  }, [lolbasEnabled]);

  // Filter data based on search and filters
  useEffect(() => {
    let filtered = lolbasData;

    // Search filter
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        entry.Name.toLowerCase().includes(query) ||
        entry.Description.toLowerCase().includes(query) ||
        entry.Commands.some(cmd => 
          cmd.Command.toLowerCase().includes(query) ||
          cmd.Description.toLowerCase().includes(query) ||
          cmd.Usecase.toLowerCase().includes(query)
        )
      );
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(entry =>
        entry.Commands.some(cmd => cmd.Category === selectedCategory)
      );
    }

    // Privileges filter
    if (selectedPrivileges !== "all") {
      filtered = filtered.filter(entry =>
        entry.Commands.some(cmd => cmd.Privileges === selectedPrivileges)
      );
    }

    setFilteredData(filtered);
  }, [lolbasData, searchTerm, selectedCategory, selectedPrivileges]);

  const handleCopyCommand = async (command: string) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedCommand(command);
      toast({
        title: "Copied!",
        description: "Command copied to clipboard",
      });
      
      setTimeout(() => {
        setCopiedCommand(null);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy command:", error);
      toast({
        title: "Error",
        description: "Failed to copy command",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Get unique categories and privileges for filters
  const categories = Array.from(new Set(lolbasData.flatMap(entry => 
    entry.Commands.map(cmd => cmd.Category)
  ))).sort();

  const privileges = Array.from(new Set(lolbasData.flatMap(entry => 
    entry.Commands.map(cmd => cmd.Privileges)
  ))).sort();

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSelectedPrivileges("all");
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading LOLBAS data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  // Check if LOLBAS is disabled
  if (lolbasEnabled === false) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="mb-6">
            <Database className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">LOLBAS Access Disabled</h2>
            <p className="text-gray-600 mb-6">
              LOLBAS access is currently disabled by the administrator. Please contact your admin or try again later.
            </p>
          </div>
          <Button
            onClick={() => navigate('/toolkits')}
            className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
          >
            Back to Toolkits
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/toolkits')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/toolkits')} className="font-['Space_Grotesk'] text-black hidden lg:flex">Back to Toolkits</Button>
              <div onClick={handleSignOut} className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"><img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {/* Mobile back button */}
          <div className="lg:hidden mb-4">
            <button 
              onClick={() => navigate('/toolkits')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity font-['Space_Grotesk']"
            >
              ← Back to Toolkits
            </button>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#a855f7] rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                  LOLBAS - Living Off The Land Binaries
                </h1>
                <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                  Windows binaries and scripts for security testing - {lolbasData.length} entries available
                </p>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search binaries, commands..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                </div>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="min-w-[150px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedPrivileges} onValueChange={setSelectedPrivileges}>
                  <SelectTrigger className="min-w-[150px]">
                    <SelectValue placeholder="Privileges" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Privileges</SelectItem>
                    {privileges.map(priv => (
                      <SelectItem key={priv} value={priv}>{priv}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(searchTerm || selectedCategory !== "all" || selectedPrivileges !== "all") && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Clear Filters
                  </Button>
                )}
              </div>
              
              {/* View Toggle */}
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="flex items-center gap-2"
                >
                  <Grid3X3 className="w-4 h-4" />
                  Cards
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="flex items-center gap-2"
                >
                  <Table className="w-4 h-4" />
                  Table
                </Button>
              </div>
            </div>
            {(searchTerm || selectedCategory !== "all" || selectedPrivileges !== "all") && (
              <div className="mt-2 text-sm text-gray-600">
                {filteredData.length} {filteredData.length === 1 ? 'entry' : 'entries'} found
                {searchTerm && ` for "${searchTerm}"`}
                {selectedCategory !== "all" && ` in category "${selectedCategory}"`}
                {selectedPrivileges !== "all" && ` with privileges "${selectedPrivileges}"`}
              </div>
            )}
          </div>

          {/* Results */}
          <div className="space-y-4">
            {filteredData.length === 0 ? (
              <div className="text-center py-12">
                <div className="mb-4">
                  <Shield className="mx-auto h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-500">
                  Try adjusting your search terms or filters.
                </p>
              </div>
            ) : viewMode === 'table' ? (
              // Table View
              <div className="overflow-x-auto border border-gray-300 rounded-lg">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900 min-w-[120px]">Binary</th>
                      <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900 min-w-[150px]">Functions</th>
                      <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900 min-w-[80px]">Type (Category)</th>
                      <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900 min-w-[120px]">ATT&CK® Techniques</th>
                      <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900 min-w-[200px]">Description</th>
                      <th className="border border-gray-300 px-3 py-3 text-left font-semibold text-gray-900 min-w-[100px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((entry, index) => (
                      <tr key={entry.Name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-300 px-3 py-3 font-medium text-gray-900">
                          {entry.Name}
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-gray-700">
                          <div className="space-y-1">
                            {entry.Commands.map((cmd, cmdIndex) => (
                              <div key={cmdIndex} className="text-sm">
                                {cmd.Category}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-gray-700">
                          {entry.Commands[0]?.Category || 'Binaries'}
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-gray-700">
                          <div className="space-y-1">
                            {entry.Commands.map((cmd, cmdIndex) => (
                              <div key={cmdIndex} className="text-sm">
                                {cmd.MitreID && (
                                  <a
                                    href={`https://attack.mitre.org/techniques/${cmd.MitreID}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                  >
                                    {cmd.MitreID} - {getTechniqueName(cmd.MitreID)}
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-gray-700 text-sm">
                          {entry.Description}
                        </td>
                        <td className="border border-gray-300 px-3 py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const commands = entry.Commands.map(cmd => cmd.Command).join('\n');
                                handleCopyCommand(commands);
                              }}
                              title="Copy all commands"
                            >
                              {copiedCommand === entry.Commands.map(cmd => cmd.Command).join('\n') ? (
                                <Check size={14} className="text-green-600" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(entry.url, '_blank')}
                              title="View details"
                            >
                              <ExternalLink size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              // Card View
              filteredData.map((entry) => (
                <Card key={entry.Name} className="border-2 border-[#E7E7E7]">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-xl text-black">{entry.Name}</CardTitle>
                          <Badge variant="outline" className="text-xs">
                            {entry.Commands.length} command{entry.Commands.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{entry.Description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Author: {entry.Author}</span>
                          <span>Created: {entry.Created}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(entry.url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {entry.Commands.map((command, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-black">{command.Usecase}</h4>
                                <Badge variant="secondary" className="text-xs">{command.Category}</Badge>
                                <Badge variant="outline" className="text-xs">{command.Privileges}</Badge>
                                {command.MitreID && (
                                  <Badge variant="destructive" className="text-xs">{command.MitreID}</Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{command.Description}</p>
                              <p className="text-xs text-gray-500">
                                OS: {command.OperatingSystem}
                              </p>
                            </div>
                            <Button
                              onClick={() => handleCopyCommand(command.Command)}
                              size="sm"
                              variant="outline"
                              className="ml-4 flex-shrink-0"
                            >
                              {copiedCommand === command.Command ? (
                                <Check size={16} className="mr-1 text-green-600" />
                              ) : (
                                <Copy size={16} className="mr-1" />
                              )}
                              {copiedCommand === command.Command ? 'Copied!' : 'Copy'}
                            </Button>
                          </div>
                          <div className="bg-black text-green-400 p-3 rounded text-sm font-mono overflow-x-auto">
                            <code>{command.Command}</code>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Results count */}
          <div className="mt-6 text-center text-sm text-gray-500">
            Showing {filteredData.length} of {lolbasData.length} entries
          </div>
        </div>
      </div>
    </div>
  );
}; 